import { useState } from "react";
import { supabase } from "../lib/supabase";
import LegalOverlay from "./LegalOverlay";

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

export default function AuthScreen({ onForgotPassword }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null); // 'terms' | 'privacy' | null

  // Indian mobile validation — accepts optional +91 prefix and 10 digits
  // starting with 6/7/8/9. Also permits generic international numbers
  // (7-15 digits) as a lenient fallback so we don't shut users out.
  const validateContact = (raw) => {
    const s = (raw || "").replace(/[\s\-()]/g, "");
    if (!s) return false;
    if (/^\+?91?[6-9]\d{9}$/.test(s)) return true;    // Indian mobile
    if (/^\+?\d{7,15}$/.test(s)) return true;         // generic international
    return false;
  };

  // Helper to ensure profile exists for user
  const ensureProfileExists = async (userId, userEmail) => {
    try {
      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id,name")
        .eq("id", userId)
        .maybeSingle();
      
      if (!existingProfile) {
        // Create profile if it doesn't exist
        await supabase.from("profiles").insert({
          id: userId,
          email: userEmail,
          name: null, // Will be set by user in Profile screen
        });
      }
    } catch (err) {
      console.error("Profile check/create error:", err);
    }
  };

  const handleSubmit = async () => {
    setError("");
    setInfo("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError("Please enter a valid email address"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (isSignUp) {
      if (!name.trim()) { setError("Please enter your name"); return; }
      if (!validateContact(contactNumber)) { setError("Please enter a valid contact number"); return; }
      if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    }

    setBusy(true);

    try {
      if (isSignUp) {
        const { data, error: authErr } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: name || "User",
            },
          },
        });
        
        if (authErr) { 
          // Filter out wrapper-related errors (from Emergent iframe interceptor)
          const errMsg = authErr.message || "";
          if (errMsg.includes("body stream") || errMsg.includes("postMessage") || 
              errMsg.includes("clone") || errMsg.includes("already used")) {
            await new Promise(r => setTimeout(r, 1000));
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData?.session) {
              setBusy(false);
              return;
            }
            // Still show a generic error if no session was created
            setError("Network error. Please try again.");
            setBusy(false);
            return;
          }
          setError(errMsg || "Sign up failed. Please try again."); 
          setBusy(false); 
          return; 
        }

        if (data.user) {
          // Create profile record — includes contact_number (Sprint 1).
          // If the `contact_number` column doesn't exist yet in this
          // Supabase project, retry without it so signup never breaks.
          const basePayload = {
            id: data.user.id,
            email,
            name: name || "User",
          };
          let { error: profileErr } = await supabase.from("profiles").upsert({
            ...basePayload,
            contact_number: (contactNumber || "").replace(/[\s\-()]/g, "") || null,
          });
          if (profileErr && /contact_number/i.test(profileErr.message || "")) {
            const retry = await supabase.from("profiles").upsert(basePayload);
            profileErr = retry.error;
          }
          if (profileErr) {
            console.error("Profile creation error:", profileErr);
          }
        }

        if (!data.session) {
          setInfo("Check your email to confirm your account, then sign in.");
          setIsSignUp(false);
          setBusy(false);
          return;
        }
      } else {
        const { data, error: authErr } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (authErr) { 
          // Filter out wrapper-related errors (from Emergent iframe interceptor)
          const errMsg = authErr.message || "";
          if (errMsg.includes("body stream") || errMsg.includes("postMessage") ||
              errMsg.includes("clone") || errMsg.includes("already used")) {
            await new Promise(r => setTimeout(r, 1000));
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData?.session) {
              // Ensure profile exists for this user
              await ensureProfileExists(sessionData.session.user.id, sessionData.session.user.email);
              setBusy(false);
              return;
            }
            setError("Network error. Please try again.");
            setBusy(false);
            return;
          }
          setError(errMsg || "Sign in failed. Please try again."); 
          setBusy(false); 
          return; 
        }
        
        // Ensure profile exists after successful login
        if (data?.user) {
          await ensureProfileExists(data.user.id, data.user.email);
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      // Filter out body stream/clone errors that come from iframe wrapper
      const errMsg = err.message || "An unexpected error occurred";
      if (errMsg.includes("body stream") || errMsg.includes("postMessage") ||
          errMsg.includes("clone") || errMsg.includes("already used")) {
        // These are wrapper-related errors, not actual auth failures
        // Check if auth actually succeeded
        await new Promise(r => setTimeout(r, 1000));
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          setBusy(false);
          return;
        }
        setError("Network error. Please try again.");
        setBusy(false);
        return;
      }
      setError(errMsg);
    }

    setBusy(false);
  };

  return (
    <div className="auth-wrapper fade-in" data-testid="auth-screen">
      <div className="auth-card" style={{ background: '#FFFFFF' }}>
        <div className="auth-logo" style={{ background: 'transparent' }}>
          <img 
            src="/logo_horizontal.jpg" 
            alt="PCOS Insights" 
            style={{ 
              maxWidth: 260, 
              width: '85%',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        <div className="auth-form" data-testid="email-auth-form">
          {isSignUp && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field"
              data-testid="input-name"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            data-testid="input-email"
          />
          {isSignUp && (
            <>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Contact Number (e.g. +91 98765 43210)"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                className="input-field"
                data-testid="input-contact-number"
              />
              <p style={{
                fontSize: 11, color: '#8A8A9A', lineHeight: 1.5,
                margin: '-4px 4px 6px', fontStyle: 'italic',
              }} data-testid="contact-privacy-note">
                We&rsquo;ll use your contact number only for important account updates and future WhatsApp reminders related to your PCOS journey. We never sell or share your personal information with third parties.
              </p>
            </>
          )}
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              data-testid="input-password"
            />
            <button
              type="button"
              className="eye-toggle"
              onClick={() => setShowPassword(!showPassword)}
              data-testid="toggle-password"
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          {isSignUp && (
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field"
              data-testid="input-confirm-password"
            />
          )}

          {!isSignUp && (
            <div style={{ textAlign: 'right', marginTop: -4, marginBottom: 8 }}>
              <button
                type="button"
                onClick={onForgotPassword}
                data-testid="forgot-password-link"
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#6D5EF5', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', textDecoration: 'none'
                }}
              >
                Forgot Password?
              </button>
            </div>
          )}

          {error && <p className="auth-error" data-testid="auth-error">{error}</p>}
          {info && <p className="auth-info" data-testid="auth-info">{info}</p>}

          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="auth-submit-btn"
          >
            {busy ? "Please wait\u2026" : isSignUp ? "Create Account" : "Sign In"}
          </button>

          <p className="auth-switch">
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <button
              className="auth-switch-link"
              onClick={() => { setIsSignUp(!isSignUp); setError(""); setInfo(""); }}
              data-testid="auth-switch-link"
            >
              {isSignUp ? "Sign In" : "Create one"}
            </button>
          </p>
        </div>

        {/* Legal links */}
        <p style={{ 
          fontSize: 11, color: '#8A8A9A', textAlign: 'center', 
          marginTop: 16, lineHeight: 1.5
        }} data-testid="auth-legal-links">
          By continuing you agree to our{' '}
          <button type="button" data-testid="link-terms"
            onClick={() => setLegalOpen('terms')}
            style={{ background: 'none', border: 'none', padding: 0, color: '#6D5EF5', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
            Terms of Service
          </button>
          {' '}and{' '}
          <button type="button" data-testid="link-privacy"
            onClick={() => setLegalOpen('privacy')}
            style={{ background: 'none', border: 'none', padding: 0, color: '#6D5EF5', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
            Privacy Policy
          </button>.
        </p>

        {/* Medical Disclaimer */}
        <p style={{ 
          fontSize: 11, color: '#8A8A9A', textAlign: 'center', 
          marginTop: 12, lineHeight: 1.5, fontStyle: 'italic' 
        }}>
          This tool provides educational insights and does not replace professional medical advice.
        </p>
      </div>
      {legalOpen && <LegalOverlay kind={legalOpen} onClose={() => setLegalOpen(null)} />}
    </div>
  );
}
